import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconBolt, IconCalendarClock } from "@tabler/icons-react";

import { Input, Text } from "@fragments";

import { SCHEDULE_MODES, getScheduleSuggestions } from "../../../helpers/schedule";
import OptionPopper from "../OptionPopper";
import { OptionRow } from "../Visibility";

/**
 * "When to publish" panel: two stacked single-choice rows — publish now, or
 * schedule. Choosing "schedule" unfolds the date and time inputs right under
 * it, with the suggestion chips beneath them. "Now" closes the panel; the
 * schedule row keeps it open so the moment can be picked.
 */
export default function SchedulePopper({
    open,
    anchorEl,
    onClose,
    schedule,
    onNow,
    onSchedule,
    onValueChange,
}) {
    const { t } = useTranslation("groupModals");

    // Chips carry `{ value, label }`; the input calls `onInput(value)` on click.
    // Relative to "now", so recomputed on every opening: the panel stays
    // mounted for the modal's lifetime and "tomorrow" must not go stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const suggestions = useMemo(() => getScheduleSuggestions(t), [t, open]);
    const isScheduled = schedule.mode === SCHEDULE_MODES.SCHEDULED;

    return (
        <OptionPopper
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            // The date (180px) and time (165px) fields sit side by side, plus
            // the section's padding.
            width={400}
            label={t("publishPost.schedule.title")}
        >
            <div className="pp-popper__header">
                <Text.subheading contrast={6}>{t("publishPost.schedule.title")}</Text.subheading>
            </div>

            <div className="pp-popper__list" role="radiogroup" aria-label={t("publishPost.schedule.title")}>
                <OptionRow
                    Icon={IconBolt}
                    label={t("publishPost.schedule.options.now.label")}
                    description={t("publishPost.schedule.options.now.description")}
                    selected={!isScheduled}
                    onSelect={() => {
                        onNow();
                        onClose();
                    }}
                />

                <OptionRow
                    Icon={IconCalendarClock}
                    label={t("publishPost.schedule.options.scheduled.label")}
                    description={t("publishPost.schedule.options.scheduled.description")}
                    selected={isScheduled}
                    onSelect={onSchedule}
                />
            </div>

            {isScheduled && (
                <div className="pp-popper__section pp-popper__schedule">
                    <Input.DateTime
                        value={schedule.value}
                        onInput={onValueChange}
                        label={t("publishPost.schedule.label")}
                        allowPast={false}
                        allowToday={true}
                        allowFuture={true}
                        suggestions={suggestions}
                    />
                </div>
            )}
        </OptionPopper>
    );
}
